import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { rooms, getRoomById } from '../config/roomTexts';
import { useLanguage } from '../context/LanguageContext';

// Placeholder images - à remplacer par les vraies photos plus tard
const roomImages = {
	double: '/images/double.jpg',
	design: '/images/design.jpg',
	family4: '/images/quadruple.jpg',
};

const RoomPreviewSection = styled.section`
  padding: ${({ theme }) => theme.spacing.xxl} 0;
`;

const RoomPreviewContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing.md};
`;

const SectionTitle = styled.h2`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  position: relative;
  padding-bottom: ${({ theme }) => theme.spacing.md};
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background-color: ${({ theme }) => theme.colors.accent};
  }
`;

const RoomsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${({ theme }) => theme.spacing.xl};
`;

const RoomCard = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: ${({ theme }) => theme.shadows.medium};
  overflow: hidden;
  transition: transform ${({ theme }) => theme.transitions.medium}, box-shadow ${({ theme }) => theme.transitions.medium};
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: ${({ theme }) => theme.shadows.large};
  }
`;

const RoomImage = styled.div`
  height: 200px;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
`;

const RoomContent = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
`;

const RoomTitle = styled.h3`
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  font-size: 1.3rem;
`;

const RoomDescription = styled.p`
  color: ${({ theme }) => theme.colors.textLight};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const RoomPrice = styled.div`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.2rem;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const RoomButton = styled(Link)`
  display: inline-block;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-weight: 600;
  text-decoration: none;
  transition: background-color ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.secondary};
  }
`;

const ViewAllButton = styled(Link)`
  display: block;
  width: fit-content;
  margin: ${({ theme }) => theme.spacing.xl} auto 0;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xl};
  background-color: transparent;
  color: ${({ theme }) => theme.colors.primary};
  border: 2px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-weight: 600;
  text-decoration: none;
  transition: all ${({ theme }) => theme.transitions.medium};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.white};
  }
`;

const RoomPreview = () => {
  const { t, language } = useLanguage();
  
  // Sélectionner 3 chambres pour l'aperçu
  const previewRooms = rooms.filter((room) => room.id === 'double' || room.id === 'design' || room.id === 'family4');
  
  return (
    <RoomPreviewSection>
      <RoomPreviewContainer>
        <SectionTitle>{t('rooms.sectionTitle')}</SectionTitle>
        <RoomsGrid>
          {previewRooms.map((room) => {
            const translatedRoom = getRoomById(language, room.id);
            return (
              <RoomCard key={room.id}>
                <RoomImage image={roomImages[room.id] || roomImages.single} />
                <RoomContent>
                  <RoomTitle>{translatedRoom?.title || room.title}</RoomTitle>
                  <RoomDescription>{translatedRoom?.description || room.description}</RoomDescription>
                  <RoomPrice>{t('rooms.priceFrom', { price: room.price })}</RoomPrice>
                  <RoomButton to={`/${language}/chambres#${room.id}`}>{t('rooms.viewDetails')}</RoomButton>
                </RoomContent>
              </RoomCard>
            );
          })}
        </RoomsGrid>
        <ViewAllButton to={`/${language}/chambres`}>{t('rooms.viewAll')}</ViewAllButton>
      </RoomPreviewContainer>
    </RoomPreviewSection>
  );
};

export default RoomPreview;
